import {PDFDocument, rgb} from 'pdf-lib';
import fontkit from '@pdf-lib/fontkit';
import {InvoiceData} from './invoiceGenerator';
import {buildXRechnungXml, embedXmlInPdf, formatValidationResult, InvoiceResult, mapLegacyInvoiceData, validateXRechnungData, XRechnungOptions} from './xrechnung';
import { generateEpcQrCodeDataUrl } from './epcQrCode';

/**
 * Template-based invoice generator using pdf-lib
 * Loads a pre-designed PDF template and overlays invoice data
 */

// A4 dimensions in points (1 point = 1/72 inch)
const PAGE_WIDTH = 595.28;
const PAGE_HEIGHT = 841.89;

// Conversion: 1mm = 2.835 points
const MM_TO_PT = 2.835;

// Cache for loaded font
let cachedDaxFont: ArrayBuffer | null = null;

/**
 * Convert mm to PDF points (origin is bottom-left in pdf-lib)
 */
const mmToPoints = (mm: number): number => mm * MM_TO_PT;

/**
 * Convert Y position from top-origin (mm) to bottom-origin (points)
 */
const yFromTop = (mmFromTop: number): number => PAGE_HEIGHT - mmToPoints(mmFromTop);

/**
 * Text positioning constants (in mm from top-left)
 * Based on the official Böhringer letterhead template (999.pdf)
 */
const POSITIONS = {
    // Left margin (matching template)
    leftMargin: 25,

    // Recipient address block
    recipientX: 25,
    recipientStartY: 63,
    recipientLineHeight: 5.5,

    // Date (right-aligned, positioned below address block)
    dateX: 185, // Right edge for date
    dateY: 100, // Below address block

    // Invoice title
    titleX: 25,
    titleY: 115,

    // Invoice details (Objekt, Tätigkeit, Leistungsdatum)
    detailsX: 25,
    detailsLabelWidth: 30,
    detailsStartY: 127,
    detailsLineHeight: 5.5,

    // Greeting
    greetingX: 25,
    greetingY: 152,

    // Line items table
    lineItemsX: 25,
    lineItemsPriceX: 120,
    lineItemsTotalX: 165,
    lineItemsStartY: 168,
    lineItemsLineHeight: 5.5,

    // Totals
    totalsX: 25,
    totalsValueX: 165,

    // Payment terms
    paymentX: 25,

    // Content width
    contentWidth: 160,
};

/**
 * Format date in German locale (DD. Month YYYY)
 */
const formatDateGerman = (date: Date): string => {
    return date.toLocaleDateString('de-DE', {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
    });
};

/**
 * Format date short (DD.MM.YYYY)
 */
const formatDateShort = (date: Date): string => {
    return date.toLocaleDateString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
    });
};

/**
 * Format currency in Euro
 */
const formatCurrency = (amount: number): string => {
    return amount.toLocaleString('de-DE', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }) + ' €';
};

/**
 * Draw text with faux-bold effect (drawing twice with slight offset)
 */
const drawBoldText = (
    page: ReturnType<typeof PDFDocument.prototype.getPages>[0],
    text: string,
    x: number,
    y: number,
    size: number,
    font: Awaited<ReturnType<typeof PDFDocument.prototype.embedFont>>,
    color: ReturnType<typeof rgb>
) => {
    // Draw text twice with slight horizontal offset to simulate bold
    page.drawText(text, {x, y, size, font, color});
    page.drawText(text, {x: x + 0.3, y, size, font, color});
};

/**
 * Validate that a buffer contains a PDF (checks for %PDF header)
 */
const isPdfBuffer = (buffer: ArrayBuffer): boolean => {
    const header = new Uint8Array(buffer.slice(0, 5));
    const pdfHeader = [0x25, 0x50, 0x44, 0x46, 0x2D]; // %PDF-
    return header.every((byte, i) => byte === pdfHeader[i]);
};

/**
 * Load the invoice template PDF based on emitter short code
 * Tries: invoice-template_{SHORT_CODE}.pdf, then falls back to invoice-template.pdf (blank)
 */
const loadTemplate = async (emitterShortCode?: string): Promise<{ buffer: ArrayBuffer; isBlank: boolean }> => {
    // Try emitter-specific template first
    if (emitterShortCode) {
        const emitterTemplateUrl = `/templates/invoice-template_${emitterShortCode}.pdf`;
        try {
            const response = await fetch(emitterTemplateUrl, {cache: 'no-cache'});
            if (response.ok) {
                const buffer = await response.arrayBuffer();
                if (isPdfBuffer(buffer)) {
                    console.log(`Loaded template for emitter: ${emitterShortCode}`);
                    return {buffer, isBlank: false};
                } else {
                    console.warn(`Template for ${emitterShortCode} returned non-PDF content, trying fallback`);
                }
            }
        } catch (e) {
            console.log(`Template for ${emitterShortCode} not found, trying fallback`);
        }
    }

    // Fallback to blank template
    const blankResponse = await fetch('/templates/invoice-template.pdf', {cache: 'no-cache'});
    if (blankResponse.ok) {
        const buffer = await blankResponse.arrayBuffer();
        if (isPdfBuffer(buffer)) {
            console.log('Using blank invoice template');
            return {buffer, isBlank: true};
        } else {
            console.error('Fallback template returned non-PDF content');
            throw new Error('Template file is not a valid PDF. Try clearing browser cache and refreshing.');
        }
    }

    throw new Error('Failed to load any invoice template. Check that template files exist in public/templates/');
};

/**
 * Load the Dax font
 */
const loadDaxFont = async (): Promise<ArrayBuffer> => {
    if (cachedDaxFont) {
        return cachedDaxFont;
    }
    const response = await fetch('/Dax-Pro-Regular.ttf');
    if (!response.ok) {
        throw new Error('Failed to load Dax font');
    }
    cachedDaxFont = await response.arrayBuffer();
    return cachedDaxFont;
};

/**
 * Create an invoice using the PDF template
 */
export const createInvoiceFromTemplate = async (
    data: InvoiceData,
    xrechnungOptions?: XRechnungOptions
): Promise<InvoiceResult> => {
    try {
        // Get emitter short code for template selection
        const emitterShortCode = data.emitter?.short_key;
        console.log(data)
        // Load the template and font
        const [templateResult, fontBytes] = await Promise.all([
            loadTemplate(emitterShortCode),
            loadDaxFont(),
        ]);

        const pdfDoc = await PDFDocument.load(templateResult.buffer);
        const isBlankTemplate = templateResult.isBlank;

        // Register fontkit for custom fonts
        pdfDoc.registerFontkit(fontkit);

        // Embed Dax font
        const font = await pdfDoc.embedFont(fontBytes);

        // Get the first page
        const pages = pdfDoc.getPages();
        const page = pages[0];

        const black = rgb(0, 0, 0);
        const white = rgb(1, 1, 1);
        const fontSize = 11;
        const smallFontSize = 9;

        let currentY = POSITIONS.recipientStartY;

        // --- Cover placeholder text areas with white rectangles ---
        // Cover entire placeholder area (address + date from template)
        // This covers: "Herr/Frau", "Straße", "PLZ Ort", and "28. Januar 2026"
        page.drawRectangle({
            x: mmToPoints(POSITIONS.leftMargin) - 5,
            y: yFromTop(POSITIONS.dateY + 10),
            width: mmToPoints(170),
            height: mmToPoints(50),
            color: white,
        });

        // --- Recipient Address ---
        if (data.customer.fullContact && data.customer.fullContact.trim()) {
            const lines = data.customer.fullContact.split('\n').filter(line => line.trim());
            for (const line of lines) {
                page.drawText(line.trim(), {
                    x: mmToPoints(POSITIONS.recipientX),
                    y: yFromTop(currentY),
                    size: fontSize,
                    font: font,
                    color: black,
                });
                currentY += POSITIONS.recipientLineHeight;
            }
        } else {
            // Customer name
            page.drawText(data.customer.name, {
                x: mmToPoints(POSITIONS.recipientX),
                y: yFromTop(currentY),
                size: fontSize,
                font: font,
                color: black,
            });
            currentY += POSITIONS.recipientLineHeight;

            // Street
            page.drawText(data.customer.street, {
                x: mmToPoints(POSITIONS.recipientX),
                y: yFromTop(currentY),
                size: fontSize,
                font: font,
                color: black,
            });
            currentY += POSITIONS.recipientLineHeight;

            // ZIP and City
            page.drawText(data.customer.zipCity, {
                x: mmToPoints(POSITIONS.recipientX),
                y: yFromTop(currentY),
                size: fontSize,
                font: font,
                color: black,
            });
        }

        // --- Date (right-aligned) ---
        const dateStr = formatDateGerman(data.invoiceDate);
        const dateWidth = font.widthOfTextAtSize(dateStr, fontSize);
        page.drawText(dateStr, {
            x: mmToPoints(POSITIONS.dateX) - dateWidth,
            y: yFromTop(POSITIONS.dateY),
            size: fontSize,
            font: font,
            color: black,
        });

        // --- Invoice Title (bold) ---
        let titleText: string;
        if (data.is_storno && data.storno_of_invoice_number) {
            titleText = `Stornorechnung Nr. ${data.invoiceNumber} zur Rechnung Nr. ${data.storno_of_invoice_number}`;
        } else {
            titleText = `Rechnung Nr. ${data.invoiceNumber}`;
        }
        drawBoldText(page, titleText, mmToPoints(POSITIONS.titleX), yFromTop(POSITIONS.titleY), 13, font, black);

        // --- Invoice Details ---
        currentY = POSITIONS.detailsStartY;
        const labelX = mmToPoints(POSITIONS.detailsX);
        const valueX = mmToPoints(POSITIONS.detailsX + POSITIONS.detailsLabelWidth);

        // Objekt (label bold)
        drawBoldText(page, 'Objekt:', labelX, yFromTop(currentY), fontSize, font, black);
        page.drawText(data.objekt, {
            x: valueX,
            y: yFromTop(currentY),
            size: fontSize,
            font: font,
            color: black,
        });
        currentY += POSITIONS.detailsLineHeight;

        if (data.durchgefuehrteArbeiten && data.durchgefuehrteArbeiten.trim() !== "") {
            // Tätigkeit (label bold, supports multiline with word wrapping)
            drawBoldText(page, 'Tätigkeit:', labelX, yFromTop(currentY), fontSize, font, black);

            // Available width for text value (from value column to right content edge)
            const maxArbeitenWidth = mmToPoints(POSITIONS.leftMargin + POSITIONS.contentWidth - POSITIONS.detailsX - POSITIONS.detailsLabelWidth);

            // Break a single word into chunks that fit within maxWidth
            const breakWord = (word: string): string[] => {
                const chunks: string[] = [];
                let remaining = word;
                while (remaining) {
                    if (font.widthOfTextAtSize(remaining, fontSize) <= maxArbeitenWidth) {
                        chunks.push(remaining);
                        break;
                    }
                    // Find how many characters fit
                    let end = remaining.length - 1;
                    while (end > 0 && font.widthOfTextAtSize(remaining.slice(0, end), fontSize) > maxArbeitenWidth) {
                        end--;
                    }
                    if (end === 0) end = 1; // At least one character per chunk
                    chunks.push(remaining.slice(0, end));
                    remaining = remaining.slice(end);
                }
                return chunks;
            };

            // First split by explicit newlines, then word-wrap each line
            const explicitLines = data.durchgefuehrteArbeiten.split('\n').filter(line => line.trim());
            for (const explicitLine of explicitLines) {
                const words = explicitLine.trim().split(' ');
                const wrappedLines: string[] = [];
                let currentLine = '';
                for (const word of words) {
                    // Check if this single word is already too wide
                    if (font.widthOfTextAtSize(word, fontSize) > maxArbeitenWidth) {
                        // Flush current line first
                        if (currentLine) {
                            wrappedLines.push(currentLine);
                            currentLine = '';
                        }
                        // Break the long word into character-level chunks
                        wrappedLines.push(...breakWord(word));
                    } else {
                        const testLine = currentLine ? `${currentLine} ${word}` : word;
                        if (font.widthOfTextAtSize(testLine, fontSize) > maxArbeitenWidth && currentLine) {
                            wrappedLines.push(currentLine);
                            currentLine = word;
                        } else {
                            currentLine = testLine;
                        }
                    }
                }
                if (currentLine) wrappedLines.push(currentLine);

                for (const wrappedLine of wrappedLines) {
                    page.drawText(wrappedLine, {
                        x: valueX,
                        y: yFromTop(currentY),
                        size: fontSize,
                        font: font,
                        color: black,
                    });
                    currentY += POSITIONS.detailsLineHeight;
                }
            }
        }
        // Leistungsdatum (label bold)
        drawBoldText(page, 'Leistungsdatum:', labelX, yFromTop(currentY), fontSize, font, black);
        page.drawText(formatDateShort(data.durchgefuehrtAm), {
            x: valueX,
            y: yFromTop(currentY),
            size: fontSize,
            font: font,
            color: black,
        });

        // --- Greeting ---
        currentY += POSITIONS.detailsLineHeight + 8;
        page.drawText('Sehr geehrte Damen und Herren,', {
            x: mmToPoints(POSITIONS.greetingX),
            y: yFromTop(currentY),
            size: fontSize,
            font: font,
            color: black,
        });
        currentY += 8;
        page.drawText('wir stellen Ihnen in Rechnung:', {
            x: mmToPoints(POSITIONS.greetingX),
            y: yFromTop(currentY),
            size: fontSize,
            font: font,
            color: black,
        });

        // --- Line Items ---
        currentY += 8;
        let subtotal = 0;

        for (const item of data.lineItems) {
            subtotal += item.total;

            // Description with quantity
            const description = `${item.quantity.toLocaleString('de-DE')} x ${item.description}`;
            page.drawText(description, {
                x: mmToPoints(POSITIONS.lineItemsX),
                y: yFromTop(currentY),
                size: fontSize,
                font: font,
                color: black,
            });

            // Unit price
            if (item.unitPrice !== undefined) {
                const priceText = formatCurrency(item.unitPrice) + (item.unit ? '/' + item.unit : '');
                page.drawText(priceText, {
                    x: mmToPoints(POSITIONS.lineItemsPriceX),
                    y: yFromTop(currentY),
                    size: fontSize,
                    font: font,
                    color: black,
                });
            }

            // Total (right-aligned)
            const totalStr = formatCurrency(item.total);
            const totalWidth = font.widthOfTextAtSize(totalStr, fontSize);
            page.drawText(totalStr, {
                x: mmToPoints(POSITIONS.lineItemsTotalX) - totalWidth,
                y: yFromTop(currentY),
                size: fontSize,
                font: font,
                color: black,
            });

            currentY += POSITIONS.lineItemsLineHeight;

            // Note if present
            if (item.note && item.note.trim()) {
                page.drawText(item.note, {
                    x: mmToPoints(POSITIONS.lineItemsX + 5),
                    y: yFromTop(currentY),
                    size: smallFontSize,
                    font: font,
                    color: rgb(0.3, 0.3, 0.3),
                });
                currentY += 5;
            }
        }

        // Line under items
        currentY += 2;
        page.drawLine({
            start: {x: mmToPoints(POSITIONS.lineItemsX), y: yFromTop(currentY)},
            end: {x: mmToPoints(POSITIONS.lineItemsTotalX), y: yFromTop(currentY)},
            thickness: 0.5,
            color: black,
        });
        currentY += 6;

        // --- Totals ---
        const vatAmount = subtotal * (data.vatRate / 100);
        const total = subtotal + vatAmount;

        // Zwischensumme
        page.drawText('Zwischensumme:', {
            x: mmToPoints(POSITIONS.totalsX),
            y: yFromTop(currentY),
            size: fontSize,
            font: font,
            color: black,
        });
        let valueStr = formatCurrency(subtotal);
        let valueWidth = font.widthOfTextAtSize(valueStr, fontSize);
        page.drawText(valueStr, {
            x: mmToPoints(POSITIONS.totalsValueX) - valueWidth,
            y: yFromTop(currentY),
            size: fontSize,
            font: font,
            color: black,
        });
        currentY += POSITIONS.lineItemsLineHeight;

        // MwSt
        page.drawText(`zzgl. ${data.vatRate}% MwSt.`, {
            x: mmToPoints(POSITIONS.totalsX),
            y: yFromTop(currentY),
            size: fontSize,
            font: font,
            color: black,
        });
        valueStr = formatCurrency(vatAmount);
        valueWidth = font.widthOfTextAtSize(valueStr, fontSize);
        page.drawText(valueStr, {
            x: mmToPoints(POSITIONS.totalsValueX) - valueWidth,
            y: yFromTop(currentY),
            size: fontSize,
            font: font,
            color: black,
        });

        // Line above total
        currentY += 4;
        page.drawLine({
            start: {x: mmToPoints(POSITIONS.totalsX), y: yFromTop(currentY)},
            end: {x: mmToPoints(POSITIONS.totalsValueX), y: yFromTop(currentY)},
            thickness: 0.5,
            color: black,
        });
        currentY += 5;

        // Gesamtsumme (bold)
        drawBoldText(page, 'Gesamtsumme:', mmToPoints(POSITIONS.totalsX), yFromTop(currentY), fontSize, font, black);
        valueStr = formatCurrency(total);
        valueWidth = font.widthOfTextAtSize(valueStr, fontSize);
        drawBoldText(page, valueStr, mmToPoints(POSITIONS.totalsValueX) - valueWidth, yFromTop(currentY), fontSize, font, black);
        currentY += 14;

        // --- Payment Terms ---
        const paymentTermsY = currentY;
        const effectivePaymentDays = data.payment_days ?? 30;
        let paymentText: string;
        if (data.skonto_percentage && data.skonto_days && data.skonto_percentage > 0 && data.skonto_days > 0) {
            const formattedPercentage = data.skonto_percentage.toLocaleString('de-DE', {
                minimumFractionDigits: 0,
                maximumFractionDigits: 2,
            });
            paymentText = `Zahlungsbedingungen: ${formattedPercentage} % Skonto bei Zahlung innerhalb von ${data.skonto_days} Tagen; netto ohne Abzug innerhalb von ${effectivePaymentDays} Tagen.`;
        } else {
            paymentText = `Zahlungsbedingungen: Zahlbar innerhalb von ${effectivePaymentDays} Tagen ohne Abzug.`;
        }
        // Payment text width
        const maxPaymentWidth = mmToPoints(POSITIONS.contentWidth);
        // Manually wrap: split into lines that fit within maxPaymentWidth
        const words = paymentText.split(' ');
        const paymentLines: string[] = [];
        let currentLine = '';
        for (const word of words) {
            const testLine = currentLine ? `${currentLine} ${word}` : word;
            if (font.widthOfTextAtSize(testLine, fontSize) > maxPaymentWidth && currentLine) {
                paymentLines.push(currentLine);
                currentLine = word;
            } else {
                currentLine = testLine;
            }
        }
        if (currentLine) paymentLines.push(currentLine);

        for (const line of paymentLines) {
            page.drawText(line, {
                x: mmToPoints(POSITIONS.paymentX),
                y: yFromTop(currentY),
                size: fontSize,
                font: font,
                color: black,
            });
            currentY += 5.5;
        }

        // --- EPC QR-Code (GiroCode) box - left aligned above footer ---
        if (!data.is_storno && data.emitter?.iban) {
            try {
                const qrDataUrl = await generateEpcQrCodeDataUrl({
                    name: data.emitter.name,
                    iban: data.emitter.iban,
                    bic: data.emitter.bic,
                    amount: total,
                    reference: `Rechnung ${data.invoiceNumber}`,
                });

                // Convert data URL to ArrayBuffer for pdf-lib
                const base64Data = qrDataUrl.split(',')[1];
                const binaryString = atob(base64Data);
                const bytes = new Uint8Array(binaryString.length);
                for (let i = 0; i < binaryString.length; i++) {
                    bytes[i] = binaryString.charCodeAt(i);
                }

                const qrImage = await pdfDoc.embedPng(bytes);

                // Box dimensions (in mm)
                const qrSizeMm = 17;
                const boxPadding = 2;
                const boxHeight = qrSizeMm + boxPadding * 2;
                const boxWidth = 58;
                const boxX = POSITIONS.leftMargin - 1;
                const footerStartY = 297 - 32; // A4 height minus footer offset
                const boxY = footerStartY - boxHeight - 3;

                // Draw rounded rectangle border
                const borderColor = rgb(220 / 255, 220 / 255, 220 / 255);
                page.drawRectangle({
                    x: mmToPoints(boxX),
                    y: yFromTop(boxY + boxHeight),
                    width: mmToPoints(boxWidth),
                    height: mmToPoints(boxHeight),
                    borderColor: borderColor,
                    borderWidth: 0.85,
                    color: undefined,
                });

                // Heading "Überweisen per Code" (faux-bold)
                const qrTextX = boxX + boxPadding;
                const qrTextY = boxY + boxPadding + 3;
                drawBoldText(page, 'Überweisen per Code', mmToPoints(qrTextX), yFromTop(qrTextY), 8, font, rgb(0, 0, 0));

                // Description text
                const descColor = rgb(60 / 255, 60 / 255, 60 / 255);
                page.drawText('Ganz bequem Code mit der', {
                    x: mmToPoints(qrTextX),
                    y: yFromTop(qrTextY + 4),
                    size: 7,
                    font: font,
                    color: descColor,
                });
                page.drawText('Banking-App scannen.', {
                    x: mmToPoints(qrTextX),
                    y: yFromTop(qrTextY + 7.5),
                    size: 7,
                    font: font,
                    color: descColor,
                });

                // QR code on the right side of the box
                const qrX = boxX + boxWidth - qrSizeMm - boxPadding;
                const qrY = boxY + boxPadding;
                const qrSizePt = mmToPoints(qrSizeMm);
                page.drawImage(qrImage, {
                    x: mmToPoints(qrX),
                    y: yFromTop(qrY + qrSizeMm),
                    width: qrSizePt,
                    height: qrSizePt,
                });
            } catch (error) {
                console.warn('Could not generate EPC QR-Code:', error);
            }
        }

        // Generate filename
        const fileName = `Rechnung_${data.invoiceNumber.replace('/', '-')}_${formatDateShort(data.invoiceDate).replace(/\./g, '-')}.pdf`;

        // Handle XRechnung if requested
        let xmlContent: string | undefined;
        let finalPdfBytes: Uint8Array;

        if (xrechnungOptions?.generateXml) {
            const xrechnungData = mapLegacyInvoiceData(data, xrechnungOptions);
            const validationResult = validateXRechnungData(xrechnungData);
            if (!validationResult.valid) {
                console.warn('XRechnung validation warnings:\n', formatValidationResult(validationResult));
            }
            xmlContent = buildXRechnungXml(xrechnungData);

            if (xrechnungOptions.embedInPdf) {
                const pdfBytes = await pdfDoc.save();
                const pdfWithXml = await embedXmlInPdf(pdfBytes.buffer as ArrayBuffer, xmlContent, data.invoiceNumber);
                finalPdfBytes = new Uint8Array(pdfWithXml);
            } else {
                finalPdfBytes = await pdfDoc.save();
            }
        } else {
            finalPdfBytes = await pdfDoc.save();
        }

        const pdfBlob = new Blob([finalPdfBytes], {type: 'application/pdf'});

        console.log('Invoice PDF generated from template:', fileName);

        return {pdfBlob, fileName, xmlContent};

    } catch (error) {
        console.error('Error generating invoice from template:', error);
        throw error;
    }
};
